import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Trash2, Trophy } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionHeader, Empty } from "@/components/ui-bits";
import { listLuckyNumbers, listDraws, performDraw, deleteDraw } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { confirmAction } from "@/lib/confirm";

export default function Draw() {
  const [numbers, setNumbers] = useState([]);
  const [draws, setDraws] = useState([]);
  const [prize, setPrize] = useState("");
  const [running, setRunning] = useState(false);
  const [displayNum, setDisplayNum] = useState(null);
  const [winner, setWinner] = useState(null);
  const intervalRef = useRef(null);

  const load = async () => {
    const [n, d] = await Promise.all([listLuckyNumbers(), listDraws()]);
    setNumbers(n); setDraws(d);
  };

  useEffect(() => { load(); }, []);

  const startDraw = async () => {
    if (numbers.length === 0) {
      toast.error("Nenhum número cadastrado");
      return;
    }
    setWinner(null);
    setRunning(true);

    // shuffle visual ~3s
    intervalRef.current = setInterval(() => {
      const r = numbers[Math.floor(Math.random() * numbers.length)];
      setDisplayNum(r.number);
    }, 80);

    try {
      const result = await performDraw(prize || null);
      // let animation run for at least 2.5s
      setTimeout(() => {
        clearInterval(intervalRef.current);
        setDisplayNum(result.winner_number);
        setWinner(result);
        setRunning(false);
        toast.success(`Sortear: ${result.winner_client_name} 🎉`);
        load();
      }, 2600);
    } catch (e) {
      clearInterval(intervalRef.current);
      setRunning(false);
      toast.error(e.response?.data?.detail || "Erro no sorteio");
    }
  };

  const removeDraw = async (id) => {
    if (!(await confirmAction("Remover este sorteio do histórico?"))) return;
    await deleteDraw(id);
    load();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      <SectionHeader
        eyebrow="Roleta da sorte"
        title="Sorteio"
        description="Cada tatuagem é uma chance. Realize seu sorteio com elegância."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Draw stage */}
        <div className="lg:col-span-2 elegant-card p-10 flex flex-col items-center justify-center text-center min-h-[420px]">
          <div className="w-14 h-14 rounded-full bg-[#F4E8D6] flex items-center justify-center mb-2">
            <Sparkles className="w-6 h-6 text-[#C4A77D]" strokeWidth={1.5} />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#5C6658]">
            {numbers.length} {numbers.length === 1 ? "número concorrendo" : "números concorrendo"}
          </p>

          <div className="my-10 h-40 flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.span
                key={displayNum ?? "idle"}
                initial={{ opacity: 0, y: 14, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -14, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                className="draw-number"
                data-testid="draw-number-display"
              >
                {displayNum ?? "—"}
              </motion.span>
            </AnimatePresence>
          </div>

          {winner && !running && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-6"
              data-testid="winner-banner"
            >
              <p className="text-xs uppercase tracking-[0.3em] text-[#C4A77D]">Vencedora</p>
              <p className="font-serif-display text-3xl text-[#2E332C] mt-2">{winner.winner_client_name}</p>
              {winner.prize && <p className="text-sm text-[#5C6658] mt-1 italic">Prêmio: {winner.prize}</p>}
            </motion.div>
          )}

          <div className="w-full max-w-md space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-wider text-[#5C6658]">Prêmio (opcional)</Label>
              <Input data-testid="prize-input" value={prize} onChange={(e) => setPrize(e.target.value)} placeholder="Ex.: Sessão grátis de 1h" className="mt-2 bg-white text-center" />
            </div>
            <Button
              onClick={startDraw}
              disabled={running || numbers.length === 0}
              data-testid="draw-button"
              className="w-full rounded-full bg-[#7B8B6F] hover:bg-[#647259] text-white py-6 text-base"
            >
              {running ? "Sorteando…" : "Realizar Sorteio"}
            </Button>
          </div>
        </div>

        {/* History */}
        <div className="elegant-card p-8">
          <div className="flex items-center gap-3 mb-6">
            <Trophy className="w-5 h-5 text-[#C4A77D]" strokeWidth={1.5} />
            <h2 className="font-serif-display text-2xl text-[#2E332C]">Histórico</h2>
          </div>
          {draws.length === 0 ? (
            <p className="text-sm text-[#5C6658]">Nenhum sorteio realizado ainda.</p>
          ) : (
            <ul className="divide-y divide-[#E8E4D9]" data-testid="draws-history">
              {draws.map((d) => (
                <li key={d.id} className="py-4 flex items-start justify-between gap-3">
                  <div>
                    <p className="font-serif-display text-lg text-[#2E332C]">{d.winner_client_name}</p>
                    <p className="text-xs text-[#5C6658] mt-1">Nº {d.winner_number} · {formatDate(d.drawn_at)}</p>
                    {d.prize && <p className="text-xs text-[#7B8B6F] italic mt-1">{d.prize}</p>}
                  </div>
                  <button onClick={() => removeDraw(d.id)} className="text-[#B86B6B] hover:text-[#8C4848]" data-testid={`remove-draw-${d.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Numbers grid */}
      <div className="elegant-card p-8 mt-6">
        <h2 className="font-serif-display text-2xl text-[#2E332C] mb-6">Todos os números cadastrados</h2>
        {numbers.length === 0 ? (
          <Empty title="Nenhum número ainda" hint="Cadastre números pelo perfil das clientes." />
        ) : (
          <div className="flex flex-wrap gap-3" data-testid="all-numbers">
            {numbers.map((n) => (
              <div key={n.id} className="px-4 py-2 rounded-full bg-[#F4E8D6]/60 border border-[#C4A77D]/30 flex items-center gap-2">
                <span className="font-serif-display text-lg text-[#7B5E2E]">{n.number}</span>
                <span className="text-xs text-[#5C6658]">{n.client_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}
